import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "./store";
import { ENDPOINTS_USER, URL_AUTH, URL_USER } from "../constants/api_endpoints";
import { axiosApi } from "../helpers";
import { IAddUserProps, IAddUserResponse, ICurrentUser, IGetUserLogsProps, IModifyUserProps, IUser, IUserLogsResponse, IUserState } from "../types/userTypes";
import { IResponse, RequestStatus, ResponseStatus } from "../types/statusTypes";

const initialState: IUserState = {
	currentUserInfo: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
		userName: null,
		fullName: null,
		role: null,
		dataset: null,
		datasets: [],
		rights: [],
		language: null,
		image: null,
		assistant: {},
	},
	userList: {
		status: RequestStatus.IDLE,
		data: null,
		dictionary: {},
	},
	userInfo: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		data: null,
		userName: null,
		userId: null,
	},
	addUser: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		id: null,
		newName: null,
	},
	modifyUser: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
		myself: false,
	},
	deleteUser: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	changePassword: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	changeDataset: {
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
	userLogs: {
		logs: [],
		status: RequestStatus.IDLE,
		error: ResponseStatus.SUCCESS,
		message: "",
	},
};

// список пользователей
export const getUserList = createAsyncThunk("user/getUserList", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse | IUser[]> = await axiosApi.get(`${URL_USER}/${ENDPOINTS_USER.LIST}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// информация о пользователе
export const getUserInfo = createAsyncThunk("user/getUserInfo", async (id: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse | IUser> = await axiosApi.get(`${URL_USER}/${ENDPOINTS_USER.GET}/${id}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// добавить пользователя
export const addUser = createAsyncThunk("user/addUser", async (data: IAddUserProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IAddUserResponse> = await axiosApi.post(`${URL_USER}/${ENDPOINTS_USER.ADD}`, {
			data: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// изменить пользователя
export const modifyUser = createAsyncThunk("user/modifyUser", async ({ data, id }: IModifyUserProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_USER}/${ENDPOINTS_USER.MODIFY}/${id}`, {
			data: JSON.stringify(data),
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// удалить пользователя
export const deleteUser = createAsyncThunk("user/deleteUser", async (id: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.delete(`${URL_USER}/${ENDPOINTS_USER.DELETE}/${id}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// информация о текущем пользователе
export const getCurrentUserInfo = createAsyncThunk("user/getCurrentUserInfo", async (_, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse | ICurrentUser> = await axiosApi.get(`${URL_USER}/${ENDPOINTS_USER.INFO}`);
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// изменить пароль текущего пользователя
export const changePassword = createAsyncThunk("user/changePassword", async (password: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_USER}/${ENDPOINTS_USER.PASSWORD}`, {
			password,
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// изменить набор данных текущего пользователя
export const changeDataset = createAsyncThunk("user/changeDataset", async (datasetId: string, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IResponse> = await axiosApi.post(`${URL_USER}/${ENDPOINTS_USER.DATASET}`, {
			dataset: datasetId,
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

// логи пользователей
export const getUserLogs = createAsyncThunk("user/getUserLogs", async ({ from, to }: IGetUserLogsProps, { rejectWithValue }) => {
	try {
		const response: AxiosResponse<IUserLogsResponse> = await axiosApi.post(`${URL_AUTH}/${ENDPOINTS_USER.USER_LOGS}`, {
			from,
			to,
		});
		return response.data;
	} catch (error) {
		if (error) {
			// возвращаем данные ошибки, пришедшие с бэка
			return rejectWithValue(error);
		}
	}
});

const userSlice = createSlice({
	name: "user",
	initialState,
	reducers: {
		// очистка списка пользователей
		clearUserList: (state) => {
			state.userList = initialState.userList;
		},
		// очистка информации о пользователе
		clearUserInfo: (state) => {
			state.userInfo = initialState.userInfo;
		},
		// очистка статуса добавления пользователя
		clearAddUserStatus: (state) => {
			state.addUser = initialState.addUser;
		},
		// очистка статуса изменения данных пользователя
		clearModifyUserStatus: (state) => {
			state.modifyUser = initialState.modifyUser;
		},
		// очистка статуса удаления пользователя
		clearDeleteUserStatus: (state) => {
			state.deleteUser = initialState.deleteUser;
		},
		// очистка информации о текущем пользователе
		clearCurrentUserInfo: (state) => {
			state.currentUserInfo = initialState.currentUserInfo;
		},
		// очистка статуса изменения пароля
		clearChangePasswordStatus: (state) => {
			state.changePassword = initialState.changePassword;
		},
		// очистка статуса изменения набора данных
		clearChangeDatasetStatus: (state) => {
			state.changeDataset = initialState.changeDataset;
		},
		// очистка логов пользователей
		clearUserLogs: (state) => {
			state.userLogs = initialState.userLogs;
		},
		// очистка всех данных, кроме инфо о текущем пользователе
		clearStateExceptCurrentUserInfo: (state) => {
			state.userList = initialState.userList;
			state.userInfo = initialState.userInfo;
			state.addUser = initialState.addUser;
			state.modifyUser = initialState.modifyUser;
			state.deleteUser = initialState.deleteUser;
			state.changePassword = initialState.changePassword;
			state.changeDataset = initialState.changeDataset;
			state.userLogs = initialState.userLogs;
		},
		// добавление имени/id пользователя
		addUserNameAndId: (state, action: PayloadAction<{ userName: string | null; userId: string | null }>) => {
			state.userInfo.userName = action.payload.userName;
			state.userInfo.userId = action.payload.userId;
		},
		// добавление имени нового пользователя
		addNewUserName: (state, action: PayloadAction<string | null>) => {
			state.addUser.newName = action.payload;
		},
		// флаг изменения данных самого себя
		modifyMyself: (state, action: PayloadAction<boolean>) => {
			state.modifyUser.myself = action.payload;
		},
		// обновление датасета текущего пользователя
		updateCurrentUserDataset: (state, action: PayloadAction<string>) => {
			state.currentUserInfo.dataset = action.payload;
		},
		// обновление данных пользователя
		updateUserInfo: (
			state,
			action: PayloadAction<
				Partial<{
					fullName: string;
					userRoleId: string;
					datasetIdDefault: string;
					datasetsListAvailable: string[];
					avatarBase64: string;
					language: string;
					destination: string;
					color: string;
				}>
			>
		) => {
			if (state.userInfo.data) {
				const { fullName, userRoleId, datasetIdDefault, datasetsListAvailable, avatarBase64, language, destination, color } = action.payload;
				if (fullName) state.userInfo.data.fullname = fullName;
				if (userRoleId) state.userInfo.data.role = userRoleId;
				if (datasetIdDefault) state.userInfo.data.dataset = datasetIdDefault;
				if (datasetsListAvailable) state.userInfo.data.datasets = datasetsListAvailable;
				if (avatarBase64) state.userInfo.data.image = avatarBase64;
				if (language) state.userInfo.data.language = language;
				if (destination) state.userInfo.data.assistant.destination = destination;
				if (color) state.userInfo.data.assistant.color = color;
			}
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getUserList.pending, (state) => {
				state.userList.status = RequestStatus.LOADING;
			})
			.addCase(getUserList.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.userList.status = RequestStatus.IDLE;
					if (Array.isArray(action.payload)) {
						state.userList.data = action.payload.sort((a, b) => {
							if (a.username > b.username) return 1;
							else if (a.username < b.username) return -1;
							else return 0;
						});
						// создаем словарь id: логин, имя
						state.userList.dictionary = Object.fromEntries(action.payload.map(({ id, username, fullname }) => [id, { login: username, fullName: fullname }]));
					} else state.userList.data = action.payload;
				} else {
					state.userList.status = RequestStatus.FAILED;
				}
			})
			.addCase(getUserList.rejected, (state, action: PayloadAction<unknown>) => {
				state.userList.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.userList.data = action.payload.response?.data;
				}
			})
			.addCase(getUserInfo.pending, (state) => {
				state.userInfo.status = RequestStatus.LOADING;
			})
			.addCase(getUserInfo.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.userInfo.status = RequestStatus.IDLE;
					if ("username" in action.payload) {
						state.userInfo.data = action.payload;
					} else if ("error" in action.payload) {
						state.userInfo.error = action.payload.error;
						state.userInfo.message = action.payload.message;
					}
				} else {
					state.userInfo.status = RequestStatus.FAILED;
				}
			})
			.addCase(getUserInfo.rejected, (state, action: PayloadAction<unknown>) => {
				state.userInfo.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.addUser.error = action.payload.response?.data.error;
					state.addUser.message = action.payload.response?.data.message;
				}
			})
			.addCase(addUser.pending, (state) => {
				state.addUser.status = RequestStatus.LOADING;
			})
			.addCase(addUser.fulfilled, (state, action) => {
				state.addUser.status = RequestStatus.IDLE;
				if (action.payload) {
					state.addUser.error = action.payload.error;
					state.addUser.message = action.payload.message;
					if (action.payload.user_id) {
						state.addUser.id = action.payload.user_id;
					}
				}
			})
			.addCase(addUser.rejected, (state, action: PayloadAction<unknown>) => {
				state.addUser.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.addUser.error = action.payload.response?.data.error;
					state.addUser.message = action.payload.response?.data.message;
				}
			})
			.addCase(modifyUser.pending, (state) => {
				state.modifyUser.status = RequestStatus.LOADING;
			})
			.addCase(modifyUser.fulfilled, (state, action) => {
				state.modifyUser.status = RequestStatus.IDLE;
				if (action.payload) {
					state.modifyUser.error = action.payload.error;
					state.modifyUser.message = action.payload.message;
				}
			})
			.addCase(modifyUser.rejected, (state, action: PayloadAction<unknown>) => {
				state.modifyUser.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.modifyUser.error = action.payload.response?.data.error;
					state.modifyUser.message = action.payload.response?.data.message;
				}
			})
			.addCase(deleteUser.pending, (state) => {
				state.deleteUser.status = RequestStatus.LOADING;
			})
			.addCase(deleteUser.fulfilled, (state, action) => {
				state.deleteUser.status = RequestStatus.IDLE;
				if (action.payload) {
					state.deleteUser.error = action.payload.error;
					state.deleteUser.message = action.payload.message;
				}
			})
			.addCase(deleteUser.rejected, (state, action: PayloadAction<unknown>) => {
				state.deleteUser.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.deleteUser.error = action.payload.response?.data.error;
					state.deleteUser.message = action.payload.response?.data.message;
				}
			})
			.addCase(getCurrentUserInfo.pending, (state) => {
				state.currentUserInfo.status = RequestStatus.LOADING;
			})
			.addCase(getCurrentUserInfo.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload !== "string") {
					state.currentUserInfo.status = RequestStatus.IDLE;
					if ("username" in action.payload) {
						state.currentUserInfo.id = action.payload.id;
						state.currentUserInfo.userName = action.payload.username;
						state.currentUserInfo.fullName = action.payload.fullname;
						state.currentUserInfo.role = action.payload.role;
						state.currentUserInfo.dataset = action.payload.dataset;
						state.currentUserInfo.datasets = action.payload.datasets;
						state.currentUserInfo.rights = action.payload.rights;
						state.currentUserInfo.language = action.payload.language;
						if (action.payload.image) state.currentUserInfo.image = action.payload.image;
						state.currentUserInfo.assistant = action.payload.assistant;
					} else if ("error" in action.payload) {
						state.currentUserInfo.error = action.payload.error;
						state.currentUserInfo.message = action.payload.message;
					}
				} else {
					state.currentUserInfo.status = RequestStatus.FAILED;
				}
			})
			.addCase(getCurrentUserInfo.rejected, (state, action: PayloadAction<unknown>) => {
				state.currentUserInfo.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.currentUserInfo.error = action.payload.response?.data.error;
					state.currentUserInfo.message = action.payload.response?.data.message;
				}
			})
			.addCase(changePassword.pending, (state) => {
				state.changePassword.status = RequestStatus.LOADING;
			})
			.addCase(changePassword.fulfilled, (state, action) => {
				state.changePassword.status = RequestStatus.IDLE;
				if (action.payload) {
					state.changePassword.error = action.payload.error;
					state.changePassword.message = action.payload.message;
				}
			})
			.addCase(changePassword.rejected, (state, action: PayloadAction<unknown>) => {
				state.changePassword.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.changePassword.error = action.payload.response?.data.error;
					state.changePassword.message = action.payload.response?.data.message;
				}
			})
			.addCase(changeDataset.pending, (state) => {
				state.changeDataset.status = RequestStatus.LOADING;
			})
			.addCase(changeDataset.fulfilled, (state, action) => {
				state.changeDataset.status = RequestStatus.IDLE;
				if (action.payload) {
					state.changeDataset.error = action.payload.error;
					state.changeDataset.message = action.payload.message;
				}
			})
			.addCase(changeDataset.rejected, (state, action: PayloadAction<unknown>) => {
				state.changeDataset.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.changeDataset.error = action.payload.response?.data.error;
					state.changeDataset.message = action.payload.response?.data.message;
				}
			})
			.addCase(getUserLogs.pending, (state) => {
				state.userLogs.status = RequestStatus.LOADING;
			})
			.addCase(getUserLogs.fulfilled, (state, action) => {
				if (action.payload && typeof action.payload === "object" && "data" in action.payload && Array.isArray(action.payload.data)) {
					state.userLogs.status = RequestStatus.IDLE;
					state.userLogs.logs = action.payload.data;
				} else state.userLogs.status = RequestStatus.FAILED;
			})
			.addCase(getUserLogs.rejected, (state, action: PayloadAction<unknown>) => {
				state.userLogs.status = RequestStatus.FAILED;
				if (action.payload instanceof AxiosError) {
					state.userLogs.error = action.payload.response?.data.error;
					state.userLogs.message = action.payload.response?.data.message;
				}
			});
	},
});

export const {
	clearUserList,
	clearUserInfo,
	clearAddUserStatus,
	clearModifyUserStatus,
	clearDeleteUserStatus,
	clearCurrentUserInfo,
	clearUserLogs,
	clearStateExceptCurrentUserInfo,
	addUserNameAndId,
	addNewUserName,
	modifyMyself,
	updateCurrentUserDataset,
	updateUserInfo,
	clearChangePasswordStatus,
	clearChangeDatasetStatus,
} = userSlice.actions;

export const selectUserList = (state: RootState) => state.user.userList;
export const selectUserInfo = (state: RootState) => state.user.userInfo;
export const selectAddUserStatus = (state: RootState) => state.user.addUser;
export const selectModifyUserStatus = (state: RootState) => state.user.modifyUser;
export const selectDeleteUserStatus = (state: RootState) => state.user.deleteUser;
export const selectCurrentUserInfo = (state: RootState) => state.user.currentUserInfo;
export const selectChangePasswordStatus = (state: RootState) => state.user.changePassword;
export const selectChangeDatasetStatus = (state: RootState) => state.user.changeDataset;
export const selectUserLogs = (state: RootState) => state.user.userLogs;

export default userSlice.reducer;
